import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * A row of counts.
 *
 * Every number rendered here came from the caller's own RLS-scoped connection, so
 * the same component shows a portfolio to an admin and one unit to a tenant with no
 * conditional anywhere. That is worth stating because it is the reason these
 * screens are cheap: nothing filters in the browser.
 *
 * `to` is optional — a stat with no destination is a fact, not a door. Maintenance
 * sees tenant counts but has nowhere to click through to.
 */
/** The fixed category hues (`--tone-*` in styles.css). */
export type StatTone =
  'blue' | 'amber' | 'green' | 'teal' | 'red' | 'purple' | 'slate' | 'pink' | 'orange';

export interface Stat {
  label: string;
  value: ReactNode;
  // `| undefined` spelled out because the project runs with
  // exactOptionalPropertyTypes: passing `to: undefined` and omitting `to` are
  // different types, and these are built by a helper that does the former.
  hint?: string | undefined;
  to?: string | undefined;
  /** What makes up this figure, revealed by the tile's disclosure arrow.
   *  A stat with `details` is a toggle, so it takes no `to`. */
  details?: ReactNode | undefined;
  /** A fixed hue for this stat's category, in place of its position in the
   *  green/blue/orange rotation. Applies to the `tile` variant. */
  tone?: StatTone | undefined;
}

/** A stat that expands to show its `details`. The whole header is the
 *  button — the arrow marks it, but the figure and label are part of the
 *  click target too, so it isn't a 16px aim. An open tile spans the full row
 *  (styles.css) so its list has room. */
function ExpandableStat({ stat }: { stat: Stat }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div
      className={open ? 'stat stat--expandable stat--open' : 'stat stat--expandable'}
      data-tone={stat.tone}
    >
      <button
        type="button"
        className="stat__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="stat__toggle-body">
          <span className="stat__value">{stat.value}</span>
          <span className="stat__label">{stat.label}</span>
          {stat.hint ? <span className="stat__hint">{stat.hint}</span> : null}
        </span>
        <svg
          className="stat__chevron"
          viewBox="0 0 16 16"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path
            d="M6 3.5 10.5 8 6 12.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <div id={panelId} className="stat__details">
          {stat.details}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Framed in a slow, rotating gradient border (`.stat-frame`) — the same
 * green/blue/orange rhythm the medallions inside already cycle through
 * (`.stat:nth-child(3n+2)`/`(3n+3)` in styles.css), so the frame reads as
 * "the ring these three colors already live on," not an unrelated stripe of
 * decoration. This is the one animated element on the dashboard, and it's
 * deliberately here: these are the numbers a staff member checks first, so
 * this is the section worth a second of motion drawing the eye — everywhere
 * else stays still. Respects `prefers-reduced-motion` (see styles.css).
 */
export function StatGrid({
  stats,
  variant = 'medallion',
}: {
  stats: Stat[];
  /** `tile` swaps the circular badges for tinted rectangles — for money
   *  figures (the property report's totals), where a full dollar amount
   *  doesn't fit a fixed-size circle. The dashboard keeps the medallions. */
  variant?: 'medallion' | 'tile';
}) {
  return (
    <div className={variant === 'tile' ? 'stat-frame stat-frame--tiles' : 'stat-frame'}>
      <div className="stat-frame__inner">
        <div className="grid">
          {stats.map((stat) => {
            if (stat.details !== undefined) {
              return <ExpandableStat key={stat.label} stat={stat} />;
            }

            const body = (
              <>
                <span className="stat__value">{stat.value}</span>
                <span className="stat__label">{stat.label}</span>
                {stat.hint ? <span className="stat__hint">{stat.hint}</span> : null}
              </>
            );

            return stat.to ? (
              <Link key={stat.label} className="stat stat--link" to={stat.to} data-tone={stat.tone}>
                {body}
              </Link>
            ) : (
              <div key={stat.label} className="stat" data-tone={stat.tone}>
                {body}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
